import { AppPageHeader, Group } from '@/components/_common';
import { useFragaProductsByPlate } from '@/core/domain/operational/Fraga/Fraga.hooks';
import { FragaProduct } from '@/core/domain/operational/Fraga/Fraga.types';
import { Divider, Image, Popover, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useFragaPartsContext } from './FragaPartsProvider';
import { useState } from 'react';
import { FaImage } from 'react-icons/fa';
import { useDebounce } from '@/hooks';

const PartsFragaList: React.FC = () => {
  const {
    workorder: { data: workorder },
  } = useWorkorderDetailContext();

  const { setFragaParts, fragaParts } = useFragaPartsContext();

  const [params, setParams] = useState<{
    take: number;
    skip: number;
    query?: string;
  }>({
    take: 10,
    skip: 0,
  });

  const currentPage = Math.round(params.skip / params.take) + 1 || 1;

  const query = useDebounce(params.query, 500);

  const { data: fragaData, isLoading } = useFragaProductsByPlate({
    ...params,
    plate: workorder?.vehicle.licensePlate,
    query,
  });

  const pageInfo = fragaData?.data.catalogSearchByPlate.pageInfo;

  const fragaProducts = fragaData?.data.catalogSearchByPlate.nodes.map(
    (node) => node.product,
  );

  const columns: ColumnsType<FragaProduct> = [
    {
      key: 'applicationDescription',
      dataIndex: 'applicationDescription',
      title: 'Descrição',
      render: (description, row) => (
        <Group gap={16}>
          <Image
            width={40}
            height={40}
            src={
              !!row?.images?.length ? row?.images[0]?.thumbnailUrl : undefined
            }
            wrapperStyle={{ flexShrink: 0 }}
            placeholder={
              <Group position="center" style={{ height: '100%' }}>
                <FaImage color="lightgray" />
              </Group>
            }
          />
          <span>{description}</span>
        </Group>
      ),
    },
    {
      title: 'Cód. Barras',
      render(_, row) {
        return (
          row?.specifications?.find(
            (item) => item.description === 'Código de barras (EAN)',
          )?.value || '-'
        );
      },
    },
    {
      title: 'NCM',
      render(_, row) {
        return (
          row?.specifications?.find((item) => item.description === 'NCM')
            ?.value || '-'
        );
      },
    },
    {
      title: 'Grupo',
      render(_, row) {
        return row?.productGroup?.family?.name || '-';
      },
    },
    {
      title: 'Fabricante',
      render(_, row) {
        return row?.brand?.name || '-';
      },
    },
  ];

  return (
    <>
      <Popover
        content={
          <div style={{ width: 'auto' }}>
            <div>Descrição</div>
            <div>Codigo de Barras</div>
            <div>NCM</div>
            <div>Grupo de peças</div>
            <div>Fabricante</div>
            <div>Modelo</div>
            <div>Marca</div>
          </div>
        }
      >
        <Group style={{ width: 'max-content', marginBottom: 16 }}>
          Dados de pesquisa
        </Group>
      </Popover>

      <AppPageHeader
        searchValue={params.query}
        onSearch={(value) =>
          setParams((prev) => ({ ...prev, query: value, skip: 0 }))
        }
      />

      <Divider />

      <Image.PreviewGroup>
        <Table
          dataSource={fragaProducts}
          columns={columns}
          loading={isLoading}
          rowKey={(row) => row.productId}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys: fragaParts.map((item) => item.productId),
            onChange(_, rows) {
              setFragaParts(rows);
            },
          }}
          pagination={{
            pageSize: params.take,
            total: pageInfo?.total,
            current: currentPage,
            pageSizeOptions: [10, 20, 50],
            onChange(page, pageSize) {
              setParams((prev) => ({
                ...prev,
                take: pageSize,
                skip: prev.take !== pageSize ? 0 : (page - 1) * pageSize,
              }));
            },
          }}
        />
      </Image.PreviewGroup>
    </>
  );
};

export default PartsFragaList;
